import {
  Alert,
  AlertIcon,
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  FormLabel,
  Text,
  Td,
} from '@chakra-ui/react';
import { Formik, FormikProps } from 'formik';
import _ from 'lodash';
import React, { useState } from 'react';

import { prettyPrintFormula } from '@finley-technologies/calc-agent';

import { colors } from 'packages/common-frontend/colors';
import { TrDraggableHandle } from 'packages/common-frontend/components/DragAndDropTable/ui';
import {
  SelectFormField,
  TextInputFormField,
} from 'packages/common-frontend/components/FormField';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { IntermediateCalcFormulaEditor } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/MonacoEditor/ui/IntermediateCalcFormulaEditor';
import { useCreditAgreementContext } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/hooks/creditAgreementContext';

const isCompleteField = (
  props: FormValues,
  intermediateCalc: CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer,
  allIntermediateCalcNames: string[],
) => {
  if (!props.field) return false;
  if (
    props.field !== intermediateCalc?.field &&
    _.includes(allIntermediateCalcNames, props.field)
  ) {
    return false;
  }

  return true;
};

const getTypeSpecificInputs = (
  formProps: FormikPropsType,
  validationErrors: string[],
  setValidationErrors: React.Dispatch<React.SetStateAction<string[]>>,
  validOrgSlugs: string[],
  operationMode?: CreditAgreement.OperationMode,
) => {
  const {
    values: { field },
  } = formProps;
  return (
    <>
      <FormLabel>Formula</FormLabel>
      {!_.isEmpty(validationErrors) && (
        <Alert status="error" mb="8px">
          <AlertIcon alignItems="start" />
          <Box>
            <AlertTitle>Errors </AlertTitle>
            <AlertDescription>
              {_.map(validationErrors, (error, index) => (
                <Text key={index}>{error}</Text>
              ))}
            </AlertDescription>
          </Box>
        </Alert>
      )}
      <Box
        border="1px"
        borderColor={colors.gray[200]}
        borderRadius="8px"
        p="8px"
      >
        <IntermediateCalcFormulaEditor
          value={formProps.values.formula}
          onChange={(value) => {
            formProps.setFieldValue('formula', value);
            if (field === 'originalOwnerSlug') {
              if (
                value &&
                value !== '"' &&
                !validOrgSlugs.map((orgSlug) => `"${orgSlug}"`).includes(value)
              ) {
                setValidationErrors([
                  `${value} is an invalid orgSlug string (make sure it's in quotes e.g. "acmeCorp")`,
                ]);
              } else {
                setValidationErrors([]);
              }
            }
          }}
          operationMode={operationMode}
        />
      </Box>
    </>
  );
};

type FormValues = {
  field: string;
  fieldType: CreditAgreement.Config.LoanTapeField.IntermediateCalcs.FieldType;
  formula: string;
};

type FormikPropsType = FormikProps<FormValues>;

const getIntermediateCalc = (props: FormValues) => {
  const intermediateCalc = {
    field: _.camelCase(props.field),
    fieldType: props.fieldType,
    config: {
      type: 'formula',
      formula: props.formula,
      engineVersion: 'hyperFormula1',
    },
  };

  return intermediateCalc;
};

type EditIntermediateCalcRowProps = {
  onCancel: Function;
  onSave: Function;
  intermediateCalc: CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer;
  allIntermediateCalcNames: string[];
  id: string;
  index: number;
  moveRow: any;
  isOrderLocked: boolean;
  operationMode?: CreditAgreement.OperationMode;
};
export const EditIntermediateCalcRow = ({
  onCancel,
  onSave,
  intermediateCalc,
  allIntermediateCalcNames,
  id,
  index,
  moveRow,
  isOrderLocked,
  operationMode,
}: EditIntermediateCalcRowProps) => {
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const { orgSlugs: validOrgSlugs } = useCreditAgreementContext();

  const ValidFieldTypes =
    CreditAgreement.Config.LoanTapeField.IntermediateCalcs.ValidFieldTypes;
  return (
    <>
      <Formik
        initialValues={{
          field: intermediateCalc?.field || '',
          fieldType: intermediateCalc?.fieldType,
          formula: prettyPrintFormula(
            intermediateCalc?.config?.formula || '"TODO"',
          ),
        }}
        onSubmit={(props) => {
          const newIntermediateCalc = getIntermediateCalc(props);
          onSave(newIntermediateCalc, setValidationErrors);
        }}
      >
        {(formProps: FormikPropsType) => (
          <TrDraggableHandle
            acceptedType="intermediateCalc"
            id={id}
            index={index}
            moveRow={moveRow}
            isDragDisabled={isOrderLocked}
          >
            <Td verticalAlign="top">
              <TextInputFormField label="Field Name" name="field" />
            </Td>
            <Td verticalAlign="top">
              <SelectFormField
                name="fieldType"
                label="Type"
                options={ValidFieldTypes.map((type) => ({
                  value: type,
                  label: type,
                }))}
              />
            </Td>
            <Td />
            <Td verticalAlign="top">
              {getTypeSpecificInputs(
                formProps,
                validationErrors,
                setValidationErrors,
                validOrgSlugs,
                operationMode,
              )}
            </Td>

            {isOrderLocked && (
              <Td verticalAlign="top">
                <Button
                  display="block"
                  w="100%"
                  mt="28px"
                  mb="16px"
                  color="white"
                  backgroundColor={colors.aurora[400]}
                  type="submit"
                  isDisabled={
                    !isCompleteField(
                      formProps.values,
                      intermediateCalc,
                      allIntermediateCalcNames,
                    )
                  }
                  onClick={() => formProps.handleSubmit()}
                >
                  Save
                </Button>
                <Button
                  display="block"
                  w="100%"
                  color={colors.aurora[400]}
                  border="2px"
                  borderRadius="8px"
                  borderColor={colors.aurora[400]}
                  backgroundColor="white"
                  onClick={() => onCancel()}
                >
                  Cancel
                </Button>
              </Td>
            )}
          </TrDraggableHandle>
        )}
      </Formik>
    </>
  );
};
